import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

const EXIT_ANIMATION_MS = 220;

export default function Drawer({ isOpen, onClose, title, footer = null, size = "sm", closeDisabled = false, children }) {
  const titleId = useId();
  const closeTimeoutRef = useRef(null);
  const openRafOneRef = useRef(null);
  const openRafTwoRef = useRef(null);
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(false);
      setIsMounted(true);
      return;
    }

    setIsVisible(false);
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsMounted(false);
      closeTimeoutRef.current = null;
    }, EXIT_ANIMATION_MS);

    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isMounted) {
      return undefined;
    }

    openRafOneRef.current = window.requestAnimationFrame(() => {
      openRafTwoRef.current = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });
    });

    return () => {
      if (openRafOneRef.current) {
        window.cancelAnimationFrame(openRafOneRef.current);
        openRafOneRef.current = null;
      }
      if (openRafTwoRef.current) {
        window.cancelAnimationFrame(openRafTwoRef.current);
        openRafTwoRef.current = null;
      }
    };
  }, [isOpen, isMounted]);

  useEffect(() => {
    if (!isMounted) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !closeDisabled) {
        onClose();
      }
    };

    document.body.classList.add("drawer-open");
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("drawer-open");
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [closeDisabled, isMounted, onClose]);

  if (!isMounted || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className={`app-drawer ${isVisible ? "is-open" : "is-closing"}`} role="presentation">
      <button className="app-drawer__backdrop" type="button" aria-label="Luk panel" onClick={closeDisabled ? undefined : onClose} />
      <section className={`app-drawer__panel app-drawer__panel--${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="app-drawer__header">
          <h2 className="app-drawer__title" id={titleId}>{title}</h2>
          <button className="app-drawer__close" type="button" aria-label="Luk" onClick={onClose} disabled={closeDisabled}>
            <i className="bi bi-x-lg"></i>
          </button>
        </header>

        <div className="app-drawer__content">
          {children}
        </div>

        {footer && (
          <footer className="app-drawer__footer">
            {footer}
          </footer>
        )}
      </section>
    </div>,
    document.body
  );
}
